// The PvP ruleset (battle design, sections 3.4-3.7 and phase 4): the Arena, Element Halls, friendly duels and Guild
// War all fight on the shared move engine (shared/combat.ts: pickMove, strike) with these rules on top.
//
// - Fight level: real up to 150, then 150 × (level/150)^0.35 (duels, tournaments) or ^0.20 (Guild War). Ranked and the
//   Element Halls fight every creature at Lv 150, up or down (atFixedLevel), so catches and gear decide, not hours played.
// - Rolls: every stat is pulled toward the same species' average-roll stat at the fight level, avg × (rolled/avg)^(1/3).
//   One rule for Talent, Quality, Bloodline and Aura (Aura counts in full under it). Stars count at their square root;
//   relics' damage at its cube root too (2026-10-20: a Q100 Totem's ×1.2 as ×1.063); refine and traits count in full.
// - HP ×3.3 (war ×30), the PvP chart (×1.1 / ×0.9, shared/elements.ts; Guild War keeps the base chart ×1.5 / ×0.67),
//   no level edge, crits 10% at ×2 and the 0.85-1 roll (shared/combat.ts CRIT).
// - Each move enters the fight with 65% of its cooldown already run; from then on its cooldown keeps running (it
//   never resets when the opposing front changes).
// - Initiative: when a creature steps in, its first beat comes at 0.45 of a beat if it is faster than the front it
//   meets, else at 0.8. Two blows due at the same moment land together (shared/arena.ts battle).
// Openings and initiative are fixed (battle-consistency.md): crits and the damage roll are the only draws, so fights
// stay turn-based on the cooldowns, a clearly stronger team wins nearly always and close fights can go either way.
// - One of each species per line-up: a second copy of a species sits out.
// - Overtime: from 90 s all damage doubles every 10 s; at 120 s the side with more creatures standing wins, then the
//   larger share of HP left, then the defender (side 1).
// Every draw comes from the fight's seed, so a fight replays exactly.
import { CONFIG } from './config.ts';
import { beatMs, kitMoves, moveCooldownMs, pickMove, strike, type Fighter, type Hit } from './combat.ts';
import { relicMult, type Relic } from './items.ts';
import { computeStats, type Stats } from './stats.ts';
import { HELD_SWITCH, heldFx, heldStats, type HeldItem, type HeldScale } from './held.ts';
import type { Rolls } from './rolls.ts';
import type { Rng } from './rng.ts';

export type PvpMode = 'arena' | 'war';

export const PVP = {
  /** Levels count in full up to here. */
  knee: 150,
  /**
   * Ranked and the Element Halls: every creature fights at this level, up or down (Cole 2026-10-20). At real levels a
   * 90-day all-day player had ×2.24 a 30-day casual's Power; at one level it is ×1.20, all catches and gear.
   */
  fixedLevel: 150,
  /** Above the knee a creature fights at knee × (level/knee)^exp. */
  levelExp: { arena: 0.35, war: 0.2 } as Record<PvpMode, number>,
  /** Rolls count at this power of their ratio to the average roll (cube root). */
  rollExp: 1 / 3,
  /** Stars count at this power of their multiplier. */
  starExp: 0.5,
  /**
   * Relic damage counts at this power of its multiplier, like rolls (2026-10-20; was 1, in full: five Q100 Totems then
   * beat the same line-up 99%, now about 80%). Older tournament games replay with 1: board rules 1-2, tournament rules 3-14.
   */
  relicExp: 1 / 3,
  /**
   * HP multiplier in the Arena, Halls and duels; Guild War uses WAR.hpMult. 3 -> 3.3 on 2026-10-15 (retune): PvP crits
   * (10% at ×2) add 10% to the average blow, so this keeps fights as long as before and Signatures still land.
   */
  hpMult: 3.3,
  /** Share of each move's cooldown already run when it enters the fight (the middle of the old seeded 30-100%). */
  openRun: 0.65,
  /** First beat, as a share of a beat, for the faster and the slower of a new pair of fronts (the middles of the old 0.3-0.6 and 0.6-1.0). */
  initFaster: 0.45,
  initSlower: 0.8,
  /** Overtime: from this point all damage doubles every overtimeDoubleMs. */
  overtimeMs: 90_000,
  overtimeDoubleMs: 10_000,
  /** The backstop. */
  capMs: 120_000,
} as const;

/** The average roll the PvP rule compares against: Talent 15, Quality 1.0, no Bloodline or Aura. */
export const AVG_ROLLS: Omit<Rolls, 'trait'> = { ivs: [15, 15, 15, 15, 15, 15], quality: 1, bloodline: 0, aura: false };

/** The level a creature fights at in PvP. Lv 300 fights as 191 in the Arena and 172 in the war. */
export function fightLevel(level: number, mode: PvpMode = 'arena'): number {
  if (level <= PVP.knee) return Math.max(1, level);
  return Math.round(PVP.knee * Math.pow(level / PVP.knee, PVP.levelExp[mode]));
}

/** A team as ranked and the Element Halls field it: every creature at PVP.fixedLevel, up or down. */
export const atFixedLevel = <T extends { level: number }>(team: readonly T[]): T[] => team.map((c) => (c.level === PVP.fixedLevel ? c : { ...c, level: PVP.fixedLevel }));

/**
 * PvP stats at a fight level: avg × (rolled/avg)^(1/3) × (1 + starMult·stars)^(1/2), stat by stat. avg is the same
 * species at the same level, trait and refine with the average roll. Aura ×3 counts as ×1.44, Bloodline V ×2 as
 * ×1.26, Quality 1.2 as ×1.06.
 */
export function pvpStats(species: string, level: number, rolls: Rolls, stars = 0, refine?: readonly number[]): Stats {
  const a = computeStats(species, level, { ...AVG_ROLLS, trait: rolls.trait }, 0, refine);
  const x = computeStats(species, level, rolls, 0, refine);
  const sm = Math.pow(1 + CONFIG.starMult * stars, PVP.starExp);
  const f = (k: keyof Stats) => Math.max(1, Math.round(a[k] * Math.pow(Math.max(1, x[k]) / Math.max(1, a[k]), PVP.rollExp) * sm));
  return { hp: f('hp'), might: f('might'), guard: f('guard'), focus: f('focus'), will: f('will'), swift: f('swift') };
}

/**
 * tablet: a Rune Tablet's element (CreatureDTO.tablet), the Coverage slot's element in every fight. held: its held
 * item (shared/held.ts), which counts in the Arena's mode only, and only while held items are switched on.
 */
export interface PvpCreature { species: string; level: number; rolls: Rolls; stars: number; relic: Relic | null; refine?: number[]; tablet?: string; held?: HeldItem | null }

/**
/**
 * A creature as it fights in PvP: fight level, PvP stats, its trait, its relic at `relicExp` (PVP.relicExp; a replay
 * of an older tournament game passes its own), and its held item (not in war; `held` off, none) under `heldScale` (the
 * board's, shared/board.ts; absent: as listed). No Hall Keeper bonus. `at`: the fight level to use instead of
 * fightLevel (the board's per-fight swing, shared/pvpfight.ts).
 */
export function pvpFighter(c: PvpCreature, mode: PvpMode = 'arena', held = HELD_SWITCH.on, heldScale?: HeldScale, at?: number, relicExp: number = PVP.relicExp): Fighter {
  const level = at ?? fightLevel(c.level, mode);
  let relicDmg: Fighter['relicDmg'];
  if (c.relic?.stat === 'element') relicDmg = { element: c.relic.element, mult: Math.pow(relicMult(c.relic), relicExp) };
  else if (c.relic?.stat === 'damage') relicDmg = { mult: Math.pow(relicMult(c.relic), relicExp) };
  let stats = pvpStats(c.species, level, c.rolls, c.stars, c.refine);
  const h = mode === 'arena' && held && c.held ? c.held : null;
  const fx = h ? heldFx(h, heldScale) : undefined;
  if (h) stats = heldStats(stats, h, heldScale);
  return { speciesId: c.species, level, stats, trait: c.rolls.trait, relicDmg, ...(c.tablet ? { tablet: c.tablet } : {}), ...(fx ? { held: fx } : {}) };
}

/** One of each species: the first of a species keeps its place, later copies sit out. */
export function oneOfEach<T extends { species: string }>(team: readonly T[]): T[] {
  const seen = new Set<string>();
  return team.filter((c) => !seen.has(c.species) && !!seen.add(c.species));
}

/** Overtime damage multiplier at `ms` into the fight: 1 until 90 s, then doubling every 10 s. */
export function overtimeMult(ms: number): number {
  return ms > PVP.overtimeMs ? Math.pow(2, (ms - PVP.overtimeMs) / PVP.overtimeDoubleMs) : 1;
}

/** Speed as the beat reads it: Swift relative to level. Initiative compares this. */
export const swiftAt = (f: Fighter) => (f.stats.swift * 105) / (Math.max(1, f.level) + 5);

/** When each of a fighter's moves is ready again: entering at `now`, each with 65% of its cooldown run. */
export function openingCooldowns(f: Fighter, now: number): Map<string, number> {
  return new Map(kitMoves(f).map((m) => [m.id, now + moveCooldownMs(f, m) * (1 - PVP.openRun)]));
}

/** The auto-cast rule on the PvP ruleset: pick the move, start its cooldown, strike. Guild War passes chart 'war'. */
export function pvpCast(att: Fighter, def: Fighter, ready: Map<string, number>, defHp: number, now: number, rng: Rng, chart: 'pvp' | 'war' = 'pvp'): Hit {
  const mv = pickMove(att, def, chart, (m) => (ready.get(m.id) ?? 0) <= now, defHp);
  if (mv.slot !== 'basic') ready.set(mv.id, now + moveCooldownMs(att, mv));
  return strike(att, def, mv, chart, rng);
}

/** The first beat of a creature stepping in against `other` (initiative), as a delay from now. Equal Speed: both wait the slower share. */
export function firstBeat(f: Fighter, other: Fighter | undefined): number {
  return beatMs(f) * (!other || swiftAt(f) > swiftAt(other) ? PVP.initFaster : PVP.initSlower);
}
